import camelCase from 'lodash/camelCase';
import mapKeys from 'lodash/mapKeys';
import { buildRelativeUrl } from '../../utils/buildUrl';
import type {
    BusinessDto,
    BusinessLiteDto,
    BusinessModel,
    ImportSource,
    BusinessStatItemDto,
    BusinessStatsDto,
    BusinessStatsModel,
    CategoryDto,
    CategoryModel,
} from '../../data-layer/business/types';
import type { BusinessUI } from '../../ui/business/types';

export enum SourceType {
    INSTAGRAM = 'instagram',
}

export enum Role {
    BASE = 'base',
    APPROVED_ADMIN = 'approved_admin',
    SUPER_ADMIN = 'super_admin',
    PENDING_APPROVAL_ADMIN = 'pending_approval_admin',
    SUPPORT = 'support',
    DECLINED_BUSINESS_ADMIN = 'declined_business_admin',
}

export enum BusinessStatus {
    PUBLISHED = 'published',
    DECLINED = 'declined',
    WITHOUT_RANGE = 'without_range',
}

const getImportSource = (sourceType: SourceType, sources?: ImportSource<SourceType>[]) => {
    const source = sources?.find((s) => s.source === sourceType);

    return source?.url
        ? {
              url: source.url,
              importFormat: source.import_format,
          }
        : undefined;
};

const getStatItem = (businessStatItemDto: BusinessStatItemDto) => ({
    value: businessStatItemDto.value,
    display: businessStatItemDto.display,
    diffValue: businessStatItemDto.diff_value,
    diffDisplay: businessStatItemDto.diff_display,
});

export const toBusinessStatsModel = (statsDto: BusinessStatsDto): BusinessStatsModel => ({
    coverage: {
        total: getStatItem(statsDto.coverage.total),
        subscribers: getStatItem(statsDto.coverage.subscribers),
        notsubscribers: getStatItem(statsDto.coverage.notsubscribers),
    },
    involved: {
        total: getStatItem(statsDto.involved.total),
        subscribers: getStatItem(statsDto.involved.subscribers),
        notsubscribers: getStatItem(statsDto.involved.notsubscribers),
    },
    shows: {
        total: getStatItem(statsDto.shows.total),
        subscribers: getStatItem(statsDto.shows.subscribers),
        notsubscribers: getStatItem(statsDto.shows.notsubscribers),
    },
    subscribers: {
        total: getStatItem(statsDto.subscribers.total),
        subscribed: getStatItem(statsDto.subscribers.subscribed),
        unsubscribed: getStatItem(statsDto.subscribers.unsubscribed),
    },
    interactions: {
        total: getStatItem(statsDto.interactions.total),
        clicks: getStatItem(statsDto.interactions.clicks),
        likes: getStatItem(statsDto.interactions.likes),
        shares: getStatItem(statsDto.interactions.shares),
        saves: getStatItem(statsDto.interactions.saves),
    },
});

const DEFAULT_COVER_ID = '28acb8e76cdc4edb9f509e3b7b2d68ee';

const getCoverUrl = (coverUrl?: string): string | undefined =>
    coverUrl?.includes(DEFAULT_COVER_ID) ? undefined : coverUrl;

const fromCategoryDto = (categoriesDto: CategoryDto[]) =>
    categoriesDto.reduce((acc: Record<number, CategoryModel>, categoryDto) => {
        acc[categoryDto.id] = {
            id: categoryDto.id,
            name: categoryDto.display_name,
        };

        return acc;
    }, {});

export const fromBusinessDto = (businessDto: BusinessDto): BusinessModel => ({
    __type: 'standard',
    url: `/businesses/${businessDto.profile.public_id}`,
    statisticUrl: `/businesses/${businessDto.profile.public_id}/management/statistic`,
    editLink: `/businesses/edit/${businessDto.profile.public_id}`,
    shareUrl: buildRelativeUrl(`/businesses/${businessDto.profile.public_id}`),
    id: businessDto.profile.id,
    publicId: businessDto.profile.public_id,
    displayName: businessDto.profile.display_name,
    coverUrl: getCoverUrl(businessDto.profile.cover_url),
    avatarUrl: businessDto.profile.avatar_url,
    subscribed: businessDto.subscribed ?? false,
    subscribers: businessDto.profile.subscribers_amount ?? 0,
    posts: businessDto.profile.posts_count ?? 0,
    description: businessDto.profile.description,
    links: mapKeys(businessDto.profile.links ?? {}, (_, key) => camelCase(key)),
    role: businessDto.role,
    createdAt: businessDto.profile.created_at,
    hideInFeed: businessDto.profile.hide_in_feed,
    offerAgreement: businessDto.profile?.offer_agreement,
    instagram: getImportSource(SourceType.INSTAGRAM, businessDto.profile?.import_settings?.sources),
    status: businessDto.profile.status,
    statusDisplay: businessDto.profile.status_display,
    categories: businessDto.profile.display_categories
        ? fromCategoryDto(businessDto.profile.display_categories)
        : {},
});

export const fromBusinessLiteDto = (businessDto: BusinessLiteDto): BusinessModel => ({
    __type: 'lite',
    url: `/businesses/${businessDto.public_id}`,
    statisticUrl: `/businesses/${businessDto.public_id}/management/statistic`,
    editLink: `/businesses/edit/${businessDto.public_id}`,
    shareUrl: buildRelativeUrl(`/businesses/${businessDto.public_id}`),
    id: businessDto.id,
    publicId: businessDto.public_id,
    avatarUrl: businessDto.avatar_url,
    displayName: businessDto.display_name,
    subscribed: businessDto.subscribed || false,
    role: businessDto.role,
    links: {},
});

export const fromBusinessUI = (businessUI: BusinessUI): BusinessModel => ({
    __type: 'standard',
    ...businessUI,
});
